/** Display helpers shared by the billing pages. */

import type { PlanBillingPeriod } from "./types";

/**
 * Locale the money formats are pinned to.
 *
 * Deliberately explicit rather than the reader's own locale: under a non-US
 * locale `Intl` disambiguates USD as `US$194.40`, which reads as a second
 * currency beside the plain `$` the plan cards and the rest of the product use.
 * Billing is quoted in one currency, so the presentation is fixed to match it.
 */
const MONEY_LOCALE = "en-US";

/**
 * Currency used before a read has said what the workspace is billed in.
 *
 * A figure has to be formatted as something while the summary is in flight, and
 * every wallet in the catalogue is in dollars.
 */
export const FALLBACK_CURRENCY = "usd";

/** Months in one annual term. */
const MONTHS_PER_YEAR = 12;

/**
 * One fee restated as what it costs per month.
 *
 * Two plans can only be compared for size once their fees cover the same span.
 * An annual fee for the smallest plan sold exceeds a monthly fee for the
 * largest, so comparing raw fees calls a downgrade an upgrade.
 */
export function monthlyEquivalent(fee: number, period: PlanBillingPeriod): number {
  return period === "annual" ? fee / MONTHS_PER_YEAR : fee;
}

/** Renders an amount as currency, keeping cents only when there are any. */
export function formatMoney(amount: number, currency: string): string {
  return amount.toLocaleString(MONEY_LOCALE, {
    style: "currency",
    currency: currency || FALLBACK_CURRENCY,
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/** Renders a count with thousands separators. */
export function formatCount(value: number): string {
  return value.toLocaleString();
}

/**
 * Renders a credit figure.
 *
 * Credits arrive priced off token counts, so they run to fractions — 32.0658 is
 * a real reading, not a rounding artefact. Two decimals keep a small turn from
 * reading as zero without putting five digits of noise on a KPI tile.
 */
export function formatCredits(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/**
 * Renders a per-credit rate. These run to fractions of a cent, so the usual
 * two-decimal currency format would collapse `$0.03` and `$0.002` to the same
 * string — the fraction digits stay wide enough to keep them apart.
 *
 * Four rather than three, because a plan priced at `$0.0075` a credit rounded to
 * `$0.008`, quoting a rate 6.7% above the one it charges.
 */
export function formatRate(rate: number, currency: string): string {
  return rate.toLocaleString(MONEY_LOCALE, {
    style: "currency",
    currency: currency || FALLBACK_CURRENCY,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

/**
 * Share of an allowance at which a quota meter starts reading as high, and where
 * the meter draws its tick.
 *
 * Stated here rather than left to `QuotaMeter`'s own default so the number in
 * the explanation and the position of the tick come from one place — a copy
 * change that drifted from the drawing would be worse than no explanation.
 */
export const WARN_RATIO = 0.75;

/** Names what the tick on a quota meter marks, in both of its two positions. */
export function quotaMarkExplanation(): string {
  return `The tick marks ${Math.round(WARN_RATIO * 100)}% of the allowance, where usage starts reading as high, or the allowance itself once usage has passed it.`;
}

/** Agrees a noun with its count, so a group of one does not read "1 features". */
export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Renders a ledger or grant date as a short, unambiguous day.
 *
 * Fixed to UTC rather than the reader's own timezone: a billing timestamp is a
 * calendar day agreed with Paddle and ClockNext, both of which place it at
 * midnight UTC, so reading it in the browser's local zone can land it a day
 * either side of what those systems agree on.
 */
export function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Renders the billing period as one line, dropping the year from the start when
 * both ends share it.
 */
export function formatPeriod(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();

  const from = start.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
    ...(sameYear ? {} : { year: "numeric" }),
  });

  return `${from} – ${formatDay(endIso)}`;
}

/** Milliseconds in a day, for phrasing how long a period has left. */
const MS_PER_DAY = 86_400_000;

/**
 * How long until a timestamp, in the roundest true words.
 *
 * A billing period's end is only useful as a distance: "4,916 credits left"
 * means something quite different with four days to go than with twenty-six.
 * Rounded up, so the last partial day still counts as a day rather than
 * disappearing.
 *
 * @param iso - When the period ends.
 * @param now - The moment to measure from.
 *
 * @returns The distance, phrased.
 */
export function formatDaysUntil(iso: string, now: Date = new Date()): string {
  const days = Math.ceil((new Date(iso).getTime() - now.getTime()) / MS_PER_DAY);

  if (days <= 0) return "Today";
  if (days === 1) return "1 day";

  return `${days} days`;
}

/**
 * How long ago a reading was taken, in the roundest true words.
 *
 * @param at - When the read landed.
 * @param now - The moment to measure from.
 *
 * @returns The age, phrased.
 */
export function formatAge(at: number, now: number = Date.now()): string {
  const minutes = Math.floor((now - at) / 60_000);

  if (minutes < 1) return "Updated just now";
  if (minutes === 1) return "Updated 1 min ago";
  if (minutes < 60) return `Updated ${minutes} min ago`;

  const hours = Math.floor(minutes / 60);

  return hours === 1 ? "Updated 1 hour ago" : `Updated ${hours} hours ago`;
}

/** Lookup key of the plan whose usage is unlimited and invoiced at the end of each month. */
const UNLIMITED_PLAN = "strive_enterprise";

/**
 * Whether a plan has no credit ceiling, so nothing about running out applies to it.
 *
 * @param plan - The workspace's current plan, or nothing when it is on none.
 *
 * @returns True on the unlimited plan.
 */
export function isUnlimitedPlan(plan: { lookup_key: string } | null | undefined): boolean {
  return plan?.lookup_key === UNLIMITED_PLAN;
}
